import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Factory, Phone, Mail, MapPin, CheckCircle, Award, Shield, Truck, 
  Package, ChevronRight, ChevronDown, Star, Users, Zap, Settings,
  Layers, Target, Globe, Building2, Wrench
} from 'lucide-react';
import WhatsAppButton from '@/components/WhatsAppButton';

const BestDataCableFactory = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Best Data Cable Factory in Noida | RSD Accessories Pvt Ltd';
    
    // Add Meta Description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'RSD Accessories is the best data cable factory in Noida offering OEM/ODM services. ISO certified USB cable manufacturer with 50,000+ daily production capacity. Get bulk quotes!');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'RSD Accessories is the best data cable factory in Noida offering OEM/ODM services. ISO certified USB cable manufacturer with 50,000+ daily production capacity. Get bulk quotes!';
      document.head.appendChild(meta);
    }
    
    // Add FAQ Schema Markup
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Who is the best data cable factory in Noida?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "RSD Accessories Pvt Ltd is widely recognized as the best data cable factory in Noida. With state-of-the-art manufacturing facilities, ISO certification, and 50,000+ units daily production capacity, we serve 500+ dealers across India with premium quality USB cables, Type-C cables, and fast charging cables."
          }
        },
        {
          "@type": "Question",
          "name": "What is the MOQ for bulk data cable orders?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our minimum order quantity (MOQ) for bulk data cable orders starts from 10,000 units. However, for new business partners, we offer flexible MOQ options starting from 5,000 units for the first order. Volume discounts are available for orders above 25,000 units."
          }
        },
        {
          "@type": "Question",
          "name": "Do you provide OEM data cable manufacturing services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, RSD Accessories provides comprehensive OEM and ODM data cable manufacturing services. We offer custom branding, private labeling, custom packaging design, and can manufacture cables to your exact specifications including length, color, material type, and connector options."
          }
        },
        {
          "@type": "Question",
          "name": "What is your data cable production capacity?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our Noida factory has a production capacity of 50,000+ data cables per day. We operate multiple production lines with automated machinery for USB to Type-C, Type-C to Type-C, Micro USB, and Lightning cables. We can scale production based on large bulk orders."
          }
        },
        {
          "@type": "Question",
          "name": "Do you manufacture silicone data cables?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we are one of the leading silicone cable manufacturers in Noida. Our silicone cables are made with premium liquid silicone rubber (LSR) that provides superior flexibility, durability, and heat resistance. Available in various colors and fast charging variants up to 120W."
          }
        },
        {
          "@type": "Question",
          "name": "What types of data cables do you manufacture?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We manufacture a complete range of data cables including USB to Type-C, USB to Micro USB, Type-C to Type-C PD cables, Lightning cables, braided nylon cables, silicone cables, 65W/120W super fast charging cables, and OEM private label cables in PVC and silicone variants."
          }
        },
        {
          "@type": "Question",
          "name": "What quality certifications does your factory have?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our factory is ISO 9001:2015 certified with BIS compliance for quality standards. All our cables undergo rigorous testing including tensile strength test, bend test, voltage drop test, and durability testing. We follow Legal Metrology guidelines for all products."
          }
        },
        {
          "@type": "Question",
          "name": "Do you supply data cables pan-India?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we supply data cables across all states in India. From our Noida factory, we serve Delhi NCR, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad, and all tier-2 and tier-3 cities. We also export to neighboring countries. Fast dispatch with reliable logistics partners."
          }
        }
      ]
    };
    
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'faq-schema';
    schemaScript.innerHTML = JSON.stringify(faqSchema);
    
    const existingSchema = document.getElementById('faq-schema');
    if (existingSchema) existingSchema.remove();
    document.head.appendChild(schemaScript);

    // Add Organization Schema
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "RSD Accessories Pvt Ltd",
      "url": "https://rsdaccessories.com",
      "logo": "/images/site/site_44.jpg",
      "description": "Best Data Cable Factory in Noida - Leading USB Cable Manufacturer offering OEM/ODM services with 50,000+ daily production capacity.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-08, Sector-57",
        "addressLocality": "Noida",
        "addressRegion": "Uttar Pradesh",
        "postalCode": "201301",
        "addressCountry": "IN"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-9311211889",
        "contactType": "sales",
        "areaServed": "IN"
      },
      "sameAs": [
        "https://www.facebook.com/rsdaccessories",
        "https://www.instagram.com/rsdaccessories"
      ]
    };
    
    const orgSchemaScript = document.createElement('script');
    orgSchemaScript.type = 'application/ld+json';
    orgSchemaScript.id = 'org-schema';
    orgSchemaScript.innerHTML = JSON.stringify(orgSchema);
    
    const existingOrgSchema = document.getElementById('org-schema');
    if (existingOrgSchema) existingOrgSchema.remove();
    document.head.appendChild(orgSchemaScript);
    
    return () => {
      const faqSchemaEl = document.getElementById('faq-schema');
      const orgSchemaEl = document.getElementById('org-schema');
      if (faqSchemaEl) faqSchemaEl.remove();
      if (orgSchemaEl) orgSchemaEl.remove();
    };
  }, []);

  const productImages = [
    {
      url: '/images/site/site_34.jpg',
      alt: 'RSD Premium Magnet Type-C Cable - Best Data Cable Factory Noida'
    },
    {
      url: '/images/site/site_30.jpg',
      alt: 'RSD Blue Braided USB to Type-C Data Cable Manufacturer Noida'
    },
    {
      url: '/images/site/site_26.jpg',
      alt: 'RSD Grey Braided Nylon Fast Charging Cable Factory Noida'
    },
    {
      url: '/images/site/site_25.jpg',
      alt: 'RSD White PVC USB Cable OEM Manufacturer India'
    },
    {
      url: '/images/site/site_32.jpg',
      alt: 'RSD Premium Silicone Fast Charging Cable Bulk Supplier Noida'
    }
  ];

  const faqs = [
    {
      question: "Who is the best data cable factory in Noida?",
      answer: "RSD Accessories Pvt Ltd is widely recognized as the best data cable factory in Noida. With state-of-the-art manufacturing facilities, ISO certification, and 50,000+ units daily production capacity, we serve 500+ dealers across India with premium quality USB cables, Type-C cables, and fast charging cables."
    },
    {
      question: "What is the MOQ for bulk data cable orders?",
      answer: "Our minimum order quantity (MOQ) for bulk data cable orders starts from 10,000 units. However, for new business partners, we offer flexible MOQ options starting from 5,000 units for the first order. Volume discounts are available for orders above 25,000 units."
    },
    {
      question: "Do you provide OEM data cable manufacturing services?",
      answer: "Yes, RSD Accessories provides comprehensive OEM and ODM data cable manufacturing services. We offer custom branding, private labeling, custom packaging design, and can manufacture cables to your exact specifications including length, color, material type, and connector options."
    },
    {
      question: "What is your data cable production capacity?",
      answer: "Our Noida factory has a production capacity of 50,000+ data cables per day. We operate multiple production lines with automated machinery for USB to Type-C, Type-C to Type-C, Micro USB, and Lightning cables. We can scale production based on large bulk orders."
    },
    {
      question: "Do you manufacture silicone data cables?",
      answer: "Yes, we are one of the leading silicone cable manufacturers in Noida. Our silicone cables are made with premium liquid silicone rubber (LSR) that provides superior flexibility, durability, and heat resistance. Available in various colors and fast charging variants up to 120W."
    },
    {
      question: "What types of data cables do you manufacture?",
      answer: "We manufacture a complete range of data cables including USB to Type-C, USB to Micro USB, Type-C to Type-C PD cables, Lightning cables, braided nylon cables, silicone cables, 65W/120W super fast charging cables, and OEM private label cables in PVC and silicone variants."
    },
    {
      question: "What quality certifications does your factory have?",
      answer: "Our factory is ISO 9001:2015 certified with BIS compliance for quality standards. All our cables undergo rigorous testing including tensile strength test, bend test, voltage drop test, and durability testing. We follow Legal Metrology guidelines for all products."
    },
    {
      question: "Do you supply data cables pan-India?",
      answer: "Yes, we supply data cables across all states in India. From our Noida factory, we serve Delhi NCR, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad, and all tier-2 and tier-3 cities. We also export to neighboring countries. Fast dispatch with reliable logistics partners."
    },
    {
      question: "What quality standards do your cables meet?",
      answer: "All our cables meet stringent quality standards with ISO 9001:2015 certification. Our cables feature 4mm OD thick construction with high-purity copper cores that provide stable data transfer and efficient power delivery. Every cable passes 100% quality inspection before dispatch."
    },
    {
      question: "How can I place a bulk order for data cables?",
      answer: "You can place bulk orders by contacting our sales team at +91-9311211889 or via WhatsApp. You can also fill out our bulk inquiry form on this page. Our team will provide you with competitive wholesale pricing based on your requirements within 24 hours."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-black py-16 md:py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, #FF4D00 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-6">
                <Link to="/" className="hover:text-white">Home</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-[#FF4D00]">Best Data Cable Factory in Noida</span>
              </nav>
              
              <div className="inline-flex items-center gap-2 bg-[#FF4D00]/20 text-[#FF4D00] px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Factory className="w-4 h-4" />
                ISO CERTIFIED MANUFACTURER
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight" data-testid="hero-h1">
                Best Data Cable Factory in Noida – <span className="text-[#FF4D00]">RSD Accessories</span>
              </h1>
              
              <p className="text-lg md:text-xl text-neutral-300 mb-6 leading-relaxed">
                RSD Accessories Pvt Ltd is India's leading <strong>data cable manufacturer in Noida</strong> with state-of-the-art production facilities. We specialize in OEM/ODM manufacturing of USB cables, Type-C cables, and fast charging cables with <strong>50,000+ units daily production capacity</strong>.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF4D00]/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                  </div>
                  <span className="text-white font-semibold">Made in India</span>
                </div>
                <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF4D00]/20 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-[#FF4D00]" />
                  </div>
                  <span className="text-white font-semibold">OEM/ODM Support</span>
                </div>
                <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF4D00]/20 flex items-center justify-center">
                    <Award className="w-5 h-5 text-[#FF4D00]" />
                  </div>
                  <span className="text-white font-semibold">ISO Certified</span>
                </div>
                <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF4D00]/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-[#FF4D00]" />
                  </div>
                  <span className="text-white font-semibold">50K+ Daily Capacity</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+919311211889">
                  <Button className="w-full sm:w-auto bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-xl font-bold">
                    <Phone className="w-5 h-5 mr-2" />
                    Contact Factory
                  </Button>
                </a>
                <a 
                  href="https://wa.me/919311211889?text=Hi, I'm interested in bulk order for data cables from your Noida factory. Please share pricing and MOQ details."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full sm:w-auto bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-6 text-lg rounded-xl font-bold">
                    Get Bulk Quote
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={productImages[0].url}
                alt={productImages[0].alt}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg">
                <p className="text-sm text-neutral-500">Trusted By</p>
                <p className="text-2xl font-black text-[#FF4D00]">500+ Dealers</p>
                <p className="text-sm text-neutral-600">Across India</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Our Factory Section */}
      <section className="py-16 md:py-20 bg-neutral-50" id="about-factory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              OUR MANUFACTURING FACILITY
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-4">
              About Our Data Cable Factory in Noida
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Located in Sector-57, Noida, Uttar Pradesh, our factory is equipped with advanced machinery and skilled workforce dedicated to producing world-class data cables.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="prose prose-lg max-w-none text-neutral-700">
                <p className="mb-6">
                  <strong>RSD Accessories Pvt Ltd</strong> operates one of the most advanced <strong>data cable manufacturing units in Noida, Delhi NCR</strong>. Our factory spans over 10,000 sq. ft. and is equipped with state-of-the-art German and Japanese machinery that ensures precision manufacturing and consistent quality across every cable we produce.
                </p>
                
                <p className="mb-6">
                  As a leading <strong>USB cable manufacturer in Noida</strong>, we have invested heavily in automated production lines that can produce over <strong>50,000 data cables daily</strong>. Our facility includes dedicated lines for PVC cables, silicone cables, braided nylon cables, and premium fast charging cables supporting up to 120W power delivery.
                </p>
                
                <p className="mb-6">
                  Our in-house quality control laboratory is equipped with advanced testing equipment including high-voltage testers, tensile strength analyzers, bend testing machines, and thermal chambers. Every batch of cables undergoes <strong>100% quality inspection</strong> before dispatch, ensuring that only premium quality products reach our distributors and dealers across India.
                </p>
                
                <p>
                  We use only <strong>high-purity copper cores</strong> that provide excellent conductivity and support fast charging protocols including Qualcomm Quick Charge, USB-PD, VOOC, and Dash Charging. Our cables feature <strong>4mm OD thick construction</strong> with reinforced strain relief for maximum durability.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <img 
                src={productImages[1].url}
                alt={productImages[1].alt}
                className="rounded-xl shadow-lg w-full h-48 object-cover"
              />
              <img 
                src={productImages[2].url}
                alt={productImages[2].alt}
                className="rounded-xl shadow-lg w-full h-48 object-cover"
              />
              <img 
                src={productImages[3].url}
                alt={productImages[3].alt}
                className="rounded-xl shadow-lg w-full h-48 object-cover"
              />
              <img 
                src={productImages[4].url}
                alt={productImages[4].alt}
                className="rounded-xl shadow-lg w-full h-48 object-cover"
              />
            </div>
          </div>
          
          {/* Factory Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-neutral-100">
              <div className="w-14 h-14 mx-auto bg-[#FF4D00]/10 rounded-full flex items-center justify-center mb-4">
                <Factory className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <p className="text-3xl font-black text-neutral-900">10,000+</p>
              <p className="text-sm text-neutral-600">Sq. Ft. Factory</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-neutral-100">
              <div className="w-14 h-14 mx-auto bg-[#FF4D00]/10 rounded-full flex items-center justify-center mb-4">
                <Wrench className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <p className="text-3xl font-black text-neutral-900">15+</p>
              <p className="text-sm text-neutral-600">Production Lines</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-neutral-100">
              <div className="w-14 h-14 mx-auto bg-[#FF4D00]/10 rounded-full flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <p className="text-3xl font-black text-neutral-900">100+</p>
              <p className="text-sm text-neutral-600">Skilled Workers</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-lg border border-neutral-100">
              <div className="w-14 h-14 mx-auto bg-[#FF4D00]/10 rounded-full flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <p className="text-3xl font-black text-neutral-900">50K+</p>
              <p className="text-sm text-neutral-600">Daily Production</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section 1 */}
      <section className="py-12 bg-[#FF4D00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">Looking for Bulk Data Cables?</h3>
              <p className="text-white/80">Get competitive wholesale pricing for your business. MOQ starts from 5,000 units.</p>
            </div>
            <div className="flex gap-4">
              <a href="tel:+919311211889">
                <Button className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-6 py-4 font-bold rounded-xl">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: 9311211889
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Product Range Section */}
      <section className="py-16 md:py-20 bg-white" id="product-range">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              OUR PRODUCT RANGE
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-4">
              Complete Range of Data Cables Manufactured in Noida
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As a leading data cable manufacturer in Noida, we produce a comprehensive range of USB cables, charging cables, and data transfer cables for all major smartphone brands.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* USB to Type C Cable */}
            <div className="bg-neutral-50 rounded-2xl p-6 hover:shadow-xl transition-all border border-neutral-100">
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">USB to Type-C Data Cable</h3>
              <p className="text-neutral-600 mb-4 leading-relaxed">
                Our USB-A to Type-C cables are the most popular product line, designed to support fast charging up to 65W and high-speed data transfer at 480Mbps. Manufactured with premium high-purity copper cores, these cables are compatible with all Android smartphones including Samsung, OnePlus, Xiaomi, Realme, OPPO, and Vivo. Available in PVC, silicone, and braided nylon variants with lengths ranging from 0.5m to 3m. Our <strong>Type C cable manufacturer unit in Noida</strong> produces over 20,000 units of these cables daily.
              </p>
              <Link to="/shop/data-cables" className="text-[#FF4D00] font-semibold hover:underline inline-flex items-center">
                View Products <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            
            {/* USB to Micro USB */}
            <div className="bg-neutral-50 rounded-2xl p-6 hover:shadow-xl transition-all border border-neutral-100">
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4">
                <Layers className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">USB to Micro USB Cable</h3>
              <p className="text-neutral-600 mb-4 leading-relaxed">
                Despite the shift to Type-C, Micro USB cables remain in high demand for budget smartphones, power banks, Bluetooth speakers, and various electronic devices. Our <strong>USB cable manufacturer unit in Noida</strong> produces high-quality Micro USB cables with reinforced connectors that withstand 10,000+ insertions. Available in fast charging variants supporting up to 18W with Quick Charge 3.0 compatibility. We offer both standard and reversible Micro USB options with premium gold-plated connectors for corrosion resistance.
              </p>
              <Link to="/shop/data-cables" className="text-[#FF4D00] font-semibold hover:underline inline-flex items-center">
                View Products <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            
            {/* Type C to Type C PD */}
            <div className="bg-neutral-50 rounded-2xl p-6 hover:shadow-xl transition-all border border-neutral-100">
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">Type-C to Type-C PD Cable</h3>
              <p className="text-neutral-600 mb-4 leading-relaxed">
                Our Type-C to Type-C Power Delivery cables are engineered for the latest smartphones and laptops that support USB-PD fast charging. These cables support power delivery up to 100W (20V/5A) and USB 3.1 data transfer speeds up to 10Gbps. Ideal for MacBook, iPad Pro, Samsung Galaxy flagship phones, and gaming laptops. Manufactured with E-Marker chips for intelligent power negotiation. As a <strong>mobile charger cable factory in Noida</strong>, we ensure these cables meet USB-IF certification standards for safety and performance.
              </p>
              <Link to="/shop/data-cables" className="text-[#FF4D00] font-semibold hover:underline inline-flex items-center">
                View Products <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            
            {/* Lightning Cable */}
            <div className="bg-neutral-50 rounded-2xl p-6 hover:shadow-xl transition-all border border-neutral-100">
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4">
                <Star className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">iPhone Lightning Cable</h3>
              <p className="text-neutral-600 mb-4 leading-relaxed">
                We manufacture premium quality Lightning cables compatible with all iPhone models from iPhone 5 onwards, including iPad and AirPods. Our Lightning cables feature MFi-grade chipsets that ensure stable charging and data sync without "This accessory may not be supported" errors. Available in USB-A to Lightning and USB-C to Lightning variants supporting fast charging up to 20W. Our braided Lightning cables are particularly popular among dealers for their durability and premium feel.
              </p>
              <Link to="/shop/data-cables" className="text-[#FF4D00] font-semibold hover:underline inline-flex items-center">
                View Products <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            
            {/* Silicone Fast Charging Cable */}
            <div className="bg-neutral-50 rounded-2xl p-6 hover:shadow-xl transition-all border border-neutral-100">
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">Silicone Fast Charging Cable</h3>
              <p className="text-neutral-600 mb-4 leading-relaxed">
                Our silicone cables are manufactured using premium liquid silicone rubber (LSR) that provides exceptional flexibility, durability, and a soft-touch feel. As a leading <strong>silicone cable manufacturer in Noida</strong>, we offer these cables in vibrant colors including white, black, blue, pink, and green. The silicone coating is heat-resistant up to 200°C and doesn't yellow over time. Available in fast charging variants supporting 65W and 120W for VOOC/Dash/Super VOOC protocols. These cables are particularly popular in retail due to their premium look and tangle-free design.
              </p>
              <Link to="/shop/data-cables" className="text-[#FF4D00] font-semibold hover:underline inline-flex items-center">
                View Products <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            
            {/* 65W/120W Super Fast Charging */}
            <div className="bg-neutral-50 rounded-2xl p-6 hover:shadow-xl transition-all border border-neutral-100">
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-[#FF4D00]" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-3">65W / 120W Super Fast Charging Cable</h3>
              <p className="text-neutral-600 mb-4 leading-relaxed">
                Our flagship super fast charging cables are designed for the latest flagship smartphones from OnePlus, OPPO, Realme, and Xiaomi that support proprietary fast charging protocols. These cables feature <strong>6A current carrying capacity</strong> with specially designed thick copper cores that minimize voltage drop during high-power charging. The 120W cables can charge compatible phones from 0-100% in just 15-20 minutes. As a <strong>fast charging cable manufacturer in Noida</strong>, we ensure these cables pass rigorous safety testing including overcurrent protection and thermal management.
              </p>
              <Link to="/shop/data-cables" className="text-[#FF4D00] font-semibold hover:underline inline-flex items-center">
                View Products <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
          
          {/* OEM Private Label */}
          <div className="mt-12 bg-gradient-to-r from-neutral-900 to-neutral-800 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-block px-4 py-2 bg-[#FF4D00] text-white font-bold rounded-full text-sm mb-4">
                  OEM MANUFACTURING
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  OEM Private Label Data Cables
                </h3>
                <p className="text-neutral-300 mb-6 leading-relaxed">
                  As an experienced <strong>OEM data cable manufacturer in India</strong>, we offer complete private labeling services for businesses looking to launch their own cable brand. Our OEM services include custom cable design, your brand logo printing on connectors and cable body, custom packaging with your branding, and dedicated production lines for large orders. We work with Amazon sellers, Flipkart sellers, retail chains, corporate gifting companies, and international exporters. Minimum order for OEM branding starts from 10,000 units.
                </p>
                <Link to="/oem-mobile-accessories-manufacturer-in-india">
                  <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-6 py-4 rounded-xl font-bold">
                    Learn About OEM Services
                  </Button>
                </Link>
              </div>
              <div className="flex justify-center">
                <img 
                  src={productImages[3].url}
                  alt="RSD OEM Data Cable Manufacturing Noida"
                  className="rounded-xl shadow-2xl max-w-sm w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Best Factory Section */}
      <section className="py-16 md:py-20 bg-neutral-50" id="why-best">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              WHY CHOOSE US
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-4">
              Why We Are the Best Data Cable Factory in Noida
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Discover what sets RSD Accessories apart as the preferred data cable manufacturer for 500+ dealers across India.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00]/30 transition-all">
              <CheckCircle className="w-10 h-10 text-[#FF4D00] mb-4" />
              <h3 className="font-bold text-lg mb-2">High Quality Copper Core</h3>
              <p className="text-neutral-600 text-sm">Premium 99.99% purity copper ensures optimal conductivity and fast charging performance.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00]/30 transition-all">
              <CheckCircle className="w-10 h-10 text-[#FF4D00] mb-4" />
              <h3 className="font-bold text-lg mb-2">Premium Copper Core</h3>
              <p className="text-neutral-600 text-sm">High-purity copper wiring supports high current flow for 65W-120W fast charging protocols.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00]/30 transition-all">
              <CheckCircle className="w-10 h-10 text-[#FF4D00] mb-4" />
              <h3 className="font-bold text-lg mb-2">4mm OD Thick Cable</h3>
              <p className="text-neutral-600 text-sm">Robust construction with reinforced strain relief for 10,000+ bend cycle durability.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00]/30 transition-all">
              <CheckCircle className="w-10 h-10 text-[#FF4D00] mb-4" />
              <h3 className="font-bold text-lg mb-2">Fast Charging Support</h3>
              <p className="text-neutral-600 text-sm">Compatible with QC 3.0, USB-PD, VOOC, Dash, Warp, and all major charging protocols.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00]/30 transition-all">
              <CheckCircle className="w-10 h-10 text-[#FF4D00] mb-4" />
              <h3 className="font-bold text-lg mb-2">BIS & Quality Standards</h3>
              <p className="text-neutral-600 text-sm">ISO 9001:2015 certified with compliance to BIS and Legal Metrology guidelines.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00]/30 transition-all">
              <CheckCircle className="w-10 h-10 text-[#FF4D00] mb-4" />
              <h3 className="font-bold text-lg mb-2">Competitive Wholesale Pricing</h3>
              <p className="text-neutral-600 text-sm">Direct factory pricing with attractive margins for distributors and bulk buyers.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00]/30 transition-all">
              <CheckCircle className="w-10 h-10 text-[#FF4D00] mb-4" />
              <h3 className="font-bold text-lg mb-2">Bulk MOQ Support</h3>
              <p className="text-neutral-600 text-sm">Flexible MOQ starting from 5,000 units with volume discounts on larger orders.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00]/30 transition-all">
              <CheckCircle className="w-10 h-10 text-[#FF4D00] mb-4" />
              <h3 className="font-bold text-lg mb-2">Private Branding</h3>
              <p className="text-neutral-600 text-sm">Complete OEM services with custom logo printing, packaging, and design support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Process Section */}
      <section className="py-16 md:py-20 bg-white" id="manufacturing-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              HOW WE MANUFACTURE
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-4">
              Our Data Cable Manufacturing Process
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              A step-by-step look at how we produce premium quality data cables at our Noida factory.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="relative">
              <div className="absolute -left-4 top-0 w-8 h-8 bg-[#FF4D00] text-white rounded-full flex items-center justify-center font-bold">1</div>
              <div className="bg-neutral-50 rounded-xl p-6 ml-6 h-full">
                <h3 className="font-bold text-lg mb-3">Raw Material Selection</h3>
                <p className="text-neutral-600">We source premium quality copper wire from certified suppliers. Only 99.99% purity copper is used to ensure optimal conductivity. PVC, TPE, and silicone materials are imported from trusted manufacturers for cable insulation.</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -left-4 top-0 w-8 h-8 bg-[#FF4D00] text-white rounded-full flex items-center justify-center font-bold">2</div>
              <div className="bg-neutral-50 rounded-xl p-6 ml-6 h-full">
                <h3 className="font-bold text-lg mb-3">Copper Wire Insulation</h3>
                <p className="text-neutral-600">Copper wires are drawn to precise specifications and then insulated with color-coded PVC coating. This ensures proper identification of data and power lines during assembly.</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -left-4 top-0 w-8 h-8 bg-[#FF4D00] text-white rounded-full flex items-center justify-center font-bold">3</div>
              <div className="bg-neutral-50 rounded-xl p-6 ml-6 h-full">
                <h3 className="font-bold text-lg mb-3">PVC / Silicone Coating</h3>
                <p className="text-neutral-600">Bundled wires are passed through extrusion machines that apply the outer jacket. For PVC cables, we use fire-retardant grade material. Silicone cables go through injection molding for seamless finish.</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -left-4 top-0 w-8 h-8 bg-[#FF4D00] text-white rounded-full flex items-center justify-center font-bold">4</div>
              <div className="bg-neutral-50 rounded-xl p-6 ml-6 h-full">
                <h3 className="font-bold text-lg mb-3">Connector Assembly</h3>
                <p className="text-neutral-600">USB-A, Type-C, Micro USB, and Lightning connectors are precision-soldered to cable ends. Gold-plated contacts ensure corrosion resistance. Strain relief boots are injection molded for durability.</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -left-4 top-0 w-8 h-8 bg-[#FF4D00] text-white rounded-full flex items-center justify-center font-bold">5</div>
              <div className="bg-neutral-50 rounded-xl p-6 ml-6 h-full">
                <h3 className="font-bold text-lg mb-3">Quality Testing</h3>
                <p className="text-neutral-600">Every cable undergoes comprehensive testing: Hi-pot test (3000V), continuity test, data transfer speed test, bend test (10,000 cycles), and tensile strength test. Only cables passing 100% QC are approved.</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -left-4 top-0 w-8 h-8 bg-[#FF4D00] text-white rounded-full flex items-center justify-center font-bold">6</div>
              <div className="bg-neutral-50 rounded-xl p-6 ml-6 h-full">
                <h3 className="font-bold text-lg mb-3">Packaging & Dispatch</h3>
                <p className="text-neutral-600">Approved cables are coiled, tagged with QC stickers, and packed in protective packaging. Custom retail boxes and bulk cartons are available. Orders are dispatched within 3-5 working days via trusted logistics.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section 2 */}
      <section className="py-12 bg-gradient-to-r from-neutral-900 to-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">Ready to Partner with Noida's Best Cable Factory?</h3>
              <p className="text-neutral-300">Contact us today for factory visit and business discussion.</p>
            </div>
            <div className="flex gap-4">
              <a 
                href="https://wa.me/919311211889?text=Hi, I'm interested in visiting your data cable factory in Noida. Please share details."
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-4 font-bold rounded-xl">
                  WhatsApp Us
                </Button>
              </a>
              <a href="mailto:contact@rsdaccessories.com">
                <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-6 py-4 font-bold rounded-xl">
                  <Mail className="w-5 h-5 mr-2" />
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* OEM / Bulk Supply Section */}
      <section className="py-16 md:py-20 bg-neutral-50" id="oem-bulk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              B2B WHOLESALE
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-4">
              OEM & Bulk Data Cable Supply from Noida
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              We serve businesses of all sizes with customized bulk cable solutions and competitive wholesale pricing.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Distributors</h3>
              <p className="text-neutral-600 text-sm">Regional and state-level distributors looking for reliable cable supply with consistent quality and competitive pricing.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Package className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Wholesalers</h3>
              <p className="text-neutral-600 text-sm">Mobile accessories wholesalers in Delhi, Greater Noida, and across NCR seeking factory-direct pricing.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Globe className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">E-commerce Sellers</h3>
              <p className="text-neutral-600 text-sm">Amazon, Flipkart, and Meesho sellers needing OEM branded cables with custom packaging for online retail.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <Truck className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Export Clients</h3>
              <p className="text-neutral-600 text-sm">International buyers from Middle East, Africa, and Southeast Asia seeking quality Made in India cables.</p>
            </div>
          </div>
          
          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-center mb-8">Our B2B Bulk Supply Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#FF4D00] flex-shrink-0" />
                <div>
                  <p className="font-semibold">MOQ 10,000+ Units</p>
                  <p className="text-sm text-neutral-600">Flexible for new partners</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#FF4D00] flex-shrink-0" />
                <div>
                  <p className="font-semibold">Custom Packaging</p>
                  <p className="text-sm text-neutral-600">Your brand, your design</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#FF4D00] flex-shrink-0" />
                <div>
                  <p className="font-semibold">Custom Branding</p>
                  <p className="text-sm text-neutral-600">Logo printing on cables</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#FF4D00] flex-shrink-0" />
                <div>
                  <p className="font-semibold">Pan India Supply</p>
                  <p className="text-sm text-neutral-600">All states covered</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose RSD Section */}
      <section className="py-16 md:py-20 bg-white" id="why-rsd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
                COMPANY CREDIBILITY
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-6">
                Why Choose RSD Accessories as Your Data Cable Partner?
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Years of Experience</h3>
                    <p className="text-neutral-600">With over 8+ years in mobile accessories manufacturing, we have the expertise to deliver quality products consistently. Our team understands the market demands of Delhi NCR and pan-India distribution.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Trusted by 500+ Dealers</h3>
                    <p className="text-neutral-600">Our dealer network spans across all major cities including Delhi, Mumbai, Kolkata, Chennai, Bangalore, Hyderabad, and hundreds of tier-2 and tier-3 cities. Long-term relationships built on trust and quality.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Truck className="w-6 h-6 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Fast Dispatch</h3>
                    <p className="text-neutral-600">Orders are processed within 24 hours and dispatched within 3-5 working days. We partner with reliable logistics companies for safe and timely delivery across India.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Transparent Pricing</h3>
                    <p className="text-neutral-600">No hidden costs. Factory-direct pricing with clear wholesale rate cards. Volume discounts clearly communicated upfront. Build your margins with confidence.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={productImages[4].url}
                alt="RSD Accessories Premium Silicone Cable"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#FF4D00] text-white rounded-xl p-6 shadow-lg">
                <p className="text-3xl font-black">8+</p>
                <p className="text-sm">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="py-16 bg-neutral-50" id="location">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              FACTORY LOCATION
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-4">
              Visit Our Data Cable Factory in Noida
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Located in Sector-57, Noida - easily accessible from Delhi, Greater Noida, and Ghaziabad. Schedule a factory visit today.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.566430440893!2d77.35!3d28.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM2JzAwLjAiTiA3N8KwMjEnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="400"
                style={{border: 0}}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="RSD Accessories Factory Location Noida"
              ></iframe>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Factory Address</h4>
                    <p className="text-neutral-600">B-08, Sector-57, Noida-201301<br/>Uttar Pradesh, India</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Phone Numbers</h4>
                    <p className="text-neutral-600">
                      <a href="tel:+919311211889" className="hover:text-[#FF4D00]">+91-9311211889</a><br/>
                      <a href="tel:+919540790030" className="hover:text-[#FF4D00]">+91-9540790030</a><br/>
                      <a href="tel:+918595459161" className="hover:text-[#FF4D00]">+91-8595459161</a>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Email</h4>
                    <p className="text-neutral-600">
                      <a href="mailto:contact@rsdaccessories.com" className="hover:text-[#FF4D00]">contact@rsdaccessories.com</a>
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-neutral-500 mb-4">
                    <strong>Service Areas:</strong> Noida, Greater Noida, Delhi NCR, Ghaziabad, Faridabad, Gurgaon, and all states across India
                  </p>
                  <a 
                    href="https://wa.me/919311211889?text=Hi, I want to schedule a visit to your data cable factory in Noida."
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-4 rounded-xl font-bold">
                      Schedule Factory Visit via WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-white" id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              FREQUENTLY ASKED QUESTIONS
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-4">
              Data Cable Factory FAQs
            </h2>
            <p className="text-lg text-neutral-600">
              Common questions about our data cable manufacturing services in Noida
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-neutral-50 rounded-xl overflow-hidden border border-neutral-200"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-neutral-100 transition-colors"
                >
                  <h3 className="font-bold text-lg pr-4">{faq.question}</h3>
                  <ChevronDown className={`w-5 h-5 text-[#FF4D00] flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-neutral-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#FF4D00] to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
            Partner with Noida's Best Data Cable Factory Today
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Get premium quality data cables at wholesale prices. MOQ starts from 5,000 units. OEM services available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919311211889">
              <Button className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg rounded-xl font-bold">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9311211889
              </Button>
            </a>
            <a 
              href="https://wa.me/919311211889?text=Hi, I want to inquire about bulk data cable orders from your Noida factory."
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white px-8 py-6 text-lg rounded-xl font-bold">
                Get WhatsApp Quote
              </Button>
            </a>
            <Link to="/become-distributor">
              <Button className="bg-neutral-900 hover:bg-neutral-800 text-white px-8 py-6 text-lg rounded-xl font-bold">
                Become a Distributor
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-12 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Explore More</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/data-cable-manufacturer-in-noida" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
              Data Cable Manufacturer Noida
            </Link>
            <span className="text-neutral-600">|</span>
            <Link to="/best-mobile-charger-manufacturer-in-noida" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
              Mobile Charger Manufacturer
            </Link>
            <span className="text-neutral-600">|</span>
            <Link to="/oem-mobile-accessories-manufacturer-in-india" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
              OEM Accessories Manufacturer
            </Link>
            <span className="text-neutral-600">|</span>
            <Link to="/shop/data-cables" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
              Shop Data Cables
            </Link>
            <span className="text-neutral-600">|</span>
            <Link to="/become-distributor" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
              Become a Distributor
            </Link>
            <span className="text-neutral-600">|</span>
            <Link to="/about" className="text-neutral-400 hover:text-[#FF4D00] transition-colors">
              About Us
            </Link>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default BestDataCableFactory;
